// src/pages/Register.js
import React, { useState } from 'react';
import LogoMark from '../components/LogoMark';
import { Link } from 'react-router-dom';
import { useStore } from '../store';
import AuthCard, { alertStyle, fullWidthBtn, linkStyle, LegalLinks } from '../components/AuthCard';
import ResendVerification from '../components/ResendVerification';

// After sign-up: verify the email, then wait for the superadmin's approval.
function CheckYourEmail({ email }) {
  return (
    <AuthCard subtitle="Almost there" width={420} footer={<Link to="/login" style={linkStyle}>Back to sign in</Link>}>
      <div style={alertStyle('success')}>
        We sent a verification link to <strong>{email}</strong>.
      </div>
      <ol style={{ fontSize: 12, color: '#4a5568', lineHeight: 1.7, paddingLeft: 18, marginBottom: 18 }}>
        <li>Open the email and click <strong>Verify email</strong>.</li>
        <li>An administrator then reviews your registration.</li>
        <li>We'll email you once it's approved — then you can sign in.</li>
      </ol>
      <div style={{ fontSize: 11, color: '#8892a4', marginBottom: 8, textAlign: 'center' }}>Didn't get it? Check your spam folder, or</div>
      <ResendVerification email={email} style={fullWidthBtn} />
    </AuthCard>
  );
}

export default function Register() {
  const { register } = useStore();
  const [form, setForm] = useState({ name: '', organization_name: '', email: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res = await register({ name: form.name, organization_name: form.organization_name.trim(), email: form.email, password: form.password });
      setRegisteredEmail(res.email);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Registration failed');
    } finally { setLoading(false); }
  };

  if (registeredEmail) return <CheckYourEmail email={registeredEmail} />;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f6fa' }}>
      <div style={{ width: 420, maxWidth: 'calc(100% - 32px)' }}>
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8eaf0', padding: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <LogoMark size={32} />
            <span style={{ fontSize: 20, fontWeight: 700, color: '#1a1f2e' }}>Post Calendar</span>
          </Link>
          <div style={{ fontSize: 13, color: '#8892a4' }}>Create your account</div>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div style={{ padding: '10px 12px', background: '#feeaea', color: '#b91c1c', borderRadius: 6, fontSize: 12, marginBottom: 14 }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">Full name</label>
            <input className="form-input" placeholder="Joy Smith" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label className="form-label">Organization name</label>
            <input className="form-input" placeholder="Your company or agency" maxLength={255} value={form.organization_name} onChange={e => setForm(f => ({ ...f, organization_name: e.target.value }))} required />
            <div style={{ fontSize: 11, color: '#8892a4', marginTop: 4 }}>You can invite your team to it after signing in.</div>
          </div>
          <div className="form-group">
            <label className="form-label">Email address</label>
            <input className="form-input" type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" placeholder="Min 8 characters" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
          </div>
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Confirm password</label>
            <input className="form-input" type="password" placeholder="Repeat password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required />
          </div>
          <p style={{ fontSize: 11, color: '#8892a4', lineHeight: 1.6, marginBottom: 12, textAlign: 'center' }}>
            By creating an account, you agree to our <Link to="/terms" style={{ color: '#0f766e' }}>Terms of Service</Link> and <Link to="/privacy" style={{ color: '#0f766e' }}>Privacy Policy</Link>.
          </p>
          <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '10px', fontSize: 14 }} disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#8892a4' }}>
          Already have an account? <Link to="/login" style={{ color: '#0f766e', fontWeight: 600 }}>Sign in</Link>
        </div>
      </div>
      <LegalLinks />
      </div>
    </div>
  );
}
